import Big from 'big.js';
import { RateTypes } from '../enum';
import { BillingRate } from '../models';
import { FormulaHelperService } from './formula-helper.service';

const CA_AK_MULT = 0.24;
const NON_CA_AK_MULT = 0.19;

export class MarginCalculatorPayBillingHelperService {
    static determineMultiplier(state: string): number {
        if (state === 'CA' || state === 'AK' || state === 'VI') {
            return CA_AK_MULT;
        } else {
            return NON_CA_AK_MULT;
        }
    }

    static convertShiftTimeToString(shiftTime?: number): string {
        switch (shiftTime) {
            case 3:
                return 'night';
            case 2:
                return 'evening';
            default:
                return 'day';
        }
    }

    static getMultiplier(type: string, shiftHours: number, billingRate: BillingRate): string {
        const multiplier = billingRate ? billingRate[`${type}Multiplier${shiftHours}`] : '';
        if (multiplier && multiplier !== '' && multiplier !== ' ') {
            return multiplier;
        } else {
            return billingRate ? billingRate[`${type}MultiplierA`] : ' ';
        }
    }

    static getMultiplierRate(type: string, shiftHours: number, billingRate: BillingRate): number {
        const rate = billingRate ? billingRate[`${type}MultAmount${shiftHours}`] : 0;

        if (rate) {
            return rate;
        } else {
            return billingRate ? billingRate[`${type}MultAmountA`] : null;
        }
    }

    static getIncluded(type: string, billingRate: BillingRate): boolean {
        type = type.charAt(0).toUpperCase() + type.slice(1);
        const included = billingRate ? Boolean(billingRate[`include${type}`]) : false;
        return included;
    }

    static shiftOvertimeRuleExists(multiplier: string, hours1: number, OverTimeFreq1: string): boolean {
        const exists: boolean =
            (multiplier && multiplier.trim() !== '') ||
            (OverTimeFreq1 && OverTimeFreq1.trim() !== '') ||
            (hours1 && hours1 > 0);
        return exists;
    }

    static calculateOTRate(facilityState: string, basePayRate: number, otPayRate: number): number {
        let otRate = 0;
        if (facilityState === 'AK' || facilityState === 'CA' || facilityState === 'VI') {
            if (otPayRate > this.calculateMinPremiumPayRate(basePayRate)) {
                otRate = this.calculateMinPremiumPayRate(basePayRate);
            } else {
                otRate = otPayRate;
            }
        } else {
            otRate = otPayRate;
        }
        return otRate;
    }

    static calculateMinPremiumPayRate(basePayRate: number): number {
        return FormulaHelperService.calculateMinPremiumPayRate(basePayRate);
    }

    static calculateBillRate(
        rateType: RateTypes,
        multiplier: string,
        multiplierAmount: number,
        baseRate: number,
        forValidation: boolean
    ): number {
        if (multiplierAmount) {
            switch (multiplier) {
                case '%':
                    return new Big(baseRate).times(multiplierAmount).round(2, Big.roundDown).toNumber();
                case '$':
                    return rateType === RateTypes.Holiday || rateType === RateTypes.Callback
                        ? new Big(baseRate).add(multiplierAmount).round(2, Big.roundDown).toNumber()
                        : multiplierAmount;
            }
        }

        return forValidation ? -1 : rateType === RateTypes.Holiday ? baseRate : 0;
    }

    static getExpectedBillRateByType(
        rateType: RateTypes,
        shiftHours: number,
        baseBillRate: number,
        billingRate: BillingRate,
        forValidation: boolean
    ): number {
        const included = MarginCalculatorPayBillingHelperService.getIncluded(
            rateType === RateTypes.OnCall ? 'OC' : rateType,
            billingRate
        );
        if (!included) {
            return rateType === RateTypes.Holiday ? baseBillRate : 0;
        }

        const multiplier = MarginCalculatorPayBillingHelperService.getMultiplier(rateType, shiftHours, billingRate);
        const multiplierRate = MarginCalculatorPayBillingHelperService.getMultiplierRate(
            rateType,
            shiftHours,
            billingRate
        );
        return MarginCalculatorPayBillingHelperService.calculateBillRate(
            rateType,
            multiplier,
            multiplierRate,
            baseBillRate,
            forValidation
        );
    }
}
